import { cx } from 'class-variance-authority';
import { DeletePhotoIcon } from '../icons/DeletePhotoIcon';
import React from 'react';
import SafeImage from '@ui/SafeImage';
import { ProductDocument } from 'models/product';
import { Play } from 'i/play';

type MediaItem =
  | ({
      type: 'image';
    } & ProductDocument['images'][0])
  | ({
      type: 'video';
    } & ProductDocument['video']);

export const MediaList = ({
  images = [],
  video,
  deleteMedia,
  onMainMediaSelect,
}: {
  images?: Array<ProductDocument['images'][0]>;
  video?: ProductDocument['video'];
  deleteMedia: (media: MediaItem, currentMedia: MediaItem[]) => void;
  onMainMediaSelect?: (media: MediaItem[]) => void;
}) => {
  const mediaList: MediaItem[] = [
    ...images.map((image) => ({
      ...image,
      type: 'image' as const,
    })),
    ...(video
      ? [
          {
            ...video,
            type: 'video' as const,
          },
        ]
      : []),
  ];

  const onDrop = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    const dropIndex = index;
    const newMedia = [...mediaList];
    const [draggedMedia] = newMedia.splice(dragIndex, 1);
    if (!draggedMedia) return;
    newMedia.splice(dropIndex, 0, draggedMedia);
    onMainMediaSelect?.(newMedia);
  };

  const onClick = (index: number) => {
    const newMedia = [...mediaList];
    const [draggedMedia] = newMedia.splice(index, 1);
    if (!draggedMedia) return;
    newMedia.unshift(draggedMedia);
    onMainMediaSelect?.(newMedia);
  };

  return (
    <div className="flex gap-4">
      {mediaList.map((media, index) => (
        <div
          draggable
          key={`${media.type}-${media.thumb}-${index}`}
          onDragStart={(e) =>
            e.dataTransfer.setData('text/plain', index.toString())
          }
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDrop(e, index)}
          onClick={() => onClick(index)}
          className={cx([
            'relative aspect-square size-[100px] overflow-hidden rounded-[2rem] md:size-36',
            index === 0 ? 'border-2 border-brand-secondary' : '',
          ])}
        >
          <MediaPreview media={media} />
          {media.type === 'video' && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/20">
              <Play className="size-12 text-white" fill="white" />
            </div>
          )}
          {index === 0 && (
            <div className="absolute bottom-0 left-0 z-20 flex  h-12 w-full items-center justify-center bg-black/70">
              <span className="text-label font-semibold text-white">Cover</span>
            </div>
          )}
          <button
            type="button"
            className="absolute right-[.6rem] top-[.6rem] z-30 flex flex-col items-center justify-center sm:hidden"
            onClick={(e) => {
              e.stopPropagation();
              deleteMedia(media, mediaList);
            }}
          >
            <DeletePhotoIcon height={20} width={20} />
          </button>
          <div className="absolute left-0 top-0 z-20 hidden aspect-square h-full w-full flex-col items-center justify-center opacity-0 transition-all hover:opacity-100 sm:flex">
            <button
              className="absolute right-2 top-2 z-30 flex cursor-pointer flex-col items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                deleteMedia(media, mediaList);
              }}
              type="button"
            >
              <DeletePhotoIcon />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const MediaPreview = React.memo(({ media }: { media: MediaItem }) => {
  if (media.type === 'video') {
    return (
      <video
        className="h-full w-full object-cover"
        src={media.download_url || media.full}
        muted
        preload="metadata"
      />
    );
  }

  return (
    <SafeImage
      alt="product media"
      className="object-cover"
      src={media.download_url}
      fallbackSrc={media.thumb}
      fill
    />
  );
});

MediaPreview.displayName = 'MediaPreview';
